// @flow
import React, { Fragment, PureComponent } from 'react';

import { withHelmet } from 'infrastructure/seo/Helmet';
import { dynamicClassName } from 'utils/dynamicClassName';

import Breadcrumb from 'components/fragments/Breadcrumb';
import CardTeam from 'containers/fragments/CardTeamContainer';
import LiveScore from 'containers/fragments/LiveScoreContainer';

import type { CardType } from 'types/PageList';

export type StateProps = {
  banniere: string,
  clubName: string,
  excerpt: string,
  featured_media: {
    src: string,
    legend: string
  },
  liste_enfants: CardType[],
  meta_description: string,
  meta_title: string,
  placeholder: string,
  rencontresLive: number,
  title: string
};

export type DispatchProps = {
  fetchPageList: (slug: string) => void,
  fetchSeason: () => void
};

type Props = StateProps & DispatchProps;

class Teams extends PureComponent<Props> {
  componentDidMount() {
    const { fetchPageList, fetchSeason } = this.props;

    fetchPageList('equipes');
    fetchSeason();
  }

  renderSubChildren = (liste_enfants: CardType[]) => {
    const { clubName } = this.props;
    return liste_enfants.map(({ object_id, title, object_slug, description, featured_media }) => (
      <CardTeam
        key={object_id}
        name={title}
        description={description}
        img={(featured_media && featured_media.src) || ''}
        link={`/equipes/${object_slug}`}
        clubName={clubName}
      />
    ));
  };

  renderChildren = () => {
    const { liste_enfants } = this.props;
    return liste_enfants.map(({ liste_enfants, title }, i) => {
      const classNames = dynamicClassName('mb-3 mb-lg-4 ft-300');
      i === 0 ? classNames.add('ft-white') : classNames.add('mt-4 mt-md-5 mt-lg-3');
      return (
        <Fragment key={title}>
          <h3 className={classNames.build()}>{title}</h3>
          <div className="row">{liste_enfants && this.renderSubChildren(liste_enfants)}</div>
        </Fragment>
      );
    });
  };

  render() {
    const {
      excerpt,
      featured_media: { src },
      banniere,
      placeholder,
      clubName,
      rencontresLive
    } = this.props;

    let bannerClassName = 'banner banner--team-cat';
    if (rencontresLive) {
      bannerClassName = 'banner banner--team-cat banner--live-score';
    }
    return (
      <>
        <section className={bannerClassName}>
          <div className="banner__thumbnail img-secondary" style={{ backgroundImage: `url(${src || banniere || placeholder})` }}></div>
          <div className="container">
            <Breadcrumb elements={[{ name: 'Accueil', to: '/' }, 'Équipes']} isDark={true} />
            <h1 className="ft-h2">{clubName ? `Equipes de rugby - ${clubName}` : 'Nos équipes'}</h1>
            <p className="banner__desc">{excerpt}</p>
          </div>
          <LiveScore />
        </section>
        <div className="container">
          <div className="page">{this.renderChildren()}</div>
        </div>
      </>
    );
  }
}

export default withHelmet(Teams)((Helmet, pageProps) => {
  const { clubName, liste_enfants, meta_title, meta_description } = pageProps;
  const teams = liste_enfants.reduce((acc, { liste_enfants: children, title: sport }) => {
    acc.push(
      ...children.map(
        ({ title: teamName }) =>
          `{
          "@context": "https://schema.org",
          "@type": "SportsTeam",
          "name": "${teamName}",
          "sport": "${sport}",
          "memberOf": [
            {
            "@type": "SportsOrganization",
            "name": "FFR"
            },
            {
            "@type": "SportsOrganization",
            "name": "${clubName}"
            }
          ]
        }`
      )
    );
    return acc;
  }, []);

  return (
    <Helmet>
      <title>{meta_title}</title>
      <meta name={'keywords'} content={`Equipes rugby ${clubName}`} />
      <link rel={'canonical'} href={window.location.href} />
      <meta name={'description'} content={meta_description} />
      <meta property={'og:title'} content={meta_title} />
      <meta property={'og:description'} content={meta_description} />
      <link rel={'canonical'} href={window.location.href} />
      <script type="application/ld+json">
        {/*@TODO: Maybe correct integration when source gives us good schemas */}
        {`[
          ${teams.join(',\n')}
        ]`}
      </script>
    </Helmet>
  );
});
